import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Cpu, Plug, Plus, X } from 'lucide-react'
import {
    KIND_LABEL,
    type ModelInput,
    type ModelRow,
    PROVIDER_TYPE_LABEL,
    type ProviderType,
    createModel,
    deleteModel,
    listModels,
    testModel,
    updateModel,
} from '~/api/models'
import { ConfirmDialog } from '~/components/ConfirmDialog'
import { DataTable } from '~/components/DataTable'
import { ModelFormDialog } from '~/components/ModelFormDialog'
import { PageHeader } from '~/components/PageHeader'
import { StatCard } from '~/components/StatCard'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Skeleton } from '~/components/ui/skeleton'
import { Spinner } from '~/components/ui/spinner'
import { useToast } from '~/components/ui/toast'

// 모델 관리(LLMOps): 모델 레지스트리 등록(모달)·연결 테스트·편집·활성 토글·삭제
// + use-case별 모델 바인딩(채팅/임베딩/비전). (DESIGN.md §5.1 /admin/models)

const PROVIDER_TYPE_TONE: Record<ProviderType, BadgeTone> = {
    managed: 'info',
    self_hosted: 'accent',
}

// 마지막 연결 테스트 결과(행 옆 배지). ok면 지연(ms)을 함께 보여준다.
interface TestState {
    ok: boolean
    latencyMs: number
}

export function ModelsPage() {
    const toast = useToast()
    const [models, setModels] = useState<ModelRow[] | null>(null)
    const [pendingDelete, setPendingDelete] = useState<ModelRow | null>(null)
    // 등록/편집 모달 상태. mode=create면 빈 폼, edit면 model로 채운다.
    const [dialog, setDialog] = useState<{
        mode: 'create' | 'edit'
        model: ModelRow | null
    } | null>(null)
    const [saving, setSaving] = useState(false)
    const [testing, setTesting] = useState<Record<string, boolean>>({})
    const [results, setResults] = useState<Record<string, TestState>>({})

    const load = () => {
        listModels()
            .then(setModels)
            .catch((e) => {
                setModels([])
                toast.error((e as Error).message)
            })
    }

    useEffect(load, [])

    const onSubmitModel = async (input: ModelInput) => {
        if (!dialog) return
        setSaving(true)
        try {
            if (dialog.mode === 'create') {
                await createModel(input)
                toast.success(`'${input.name}' 모델을 등록했어요.`)
            } else if (dialog.model) {
                await updateModel(dialog.model.id, input)
                toast.success(`'${input.name}' 모델을 수정했어요.`)
            }
            setDialog(null)
            load()
        } catch (err) {
            toast.error((err as Error).message)
        } finally {
            setSaving(false)
        }
    }

    const toggleActive = async (model: ModelRow) => {
        try {
            await updateModel(model.id, { active: !model.active })
            toast.success(
                `'${model.name}' 모델을 ${model.active ? '비활성화' : '활성화'}했어요.`
            )
            load()
        } catch (err) {
            toast.error((err as Error).message)
        }
    }

    const runTest = async (model: ModelRow) => {
        setTesting((t) => ({ ...t, [model.id]: true }))
        try {
            const res = await testModel(model.id)
            setResults((r) => ({
                ...r,
                [model.id]: { ok: res.ok, latencyMs: res.latencyMs },
            }))
            if (res.ok) {
                toast.success(`'${model.name}' 연결 성공 (${res.latencyMs}ms)`)
            } else {
                toast.error(
                    `'${model.name}' 연결 실패: ${res.error ?? '알 수 없는 오류'}`
                )
            }
        } catch (err) {
            setResults((r) => ({
                ...r,
                [model.id]: { ok: false, latencyMs: 0 },
            }))
            toast.error((err as Error).message)
        } finally {
            setTesting((t) => ({ ...t, [model.id]: false }))
        }
    }

    const confirmRemoveModel = async () => {
        if (!pendingDelete) return
        const { id, name } = pendingDelete
        setPendingDelete(null)
        try {
            await deleteModel(id)
            toast.success(`'${name}' 모델을 삭제했어요.`)
            load()
        } catch (err) {
            toast.error((err as Error).message)
        }
    }

    const total = models?.length ?? 0
    const activeCount = models?.filter((m) => m.active).length ?? 0

    return (
        <div>
            <PageHeader
                title='모델 관리'
                description='모델 레지스트리 등록 · 연결 테스트 · 활성 토글 · 삭제 (use-case 바인딩은 「기능 카탈로그」에서)'
            />

            {/* 요약: 등록 수 · 활성 수 */}
            <div className='mb-5 grid max-w-md grid-cols-2 gap-4'>
                <StatCard label='등록 모델' value={total} icon={Cpu} />
                <StatCard
                    label='활성 모델'
                    value={activeCount}
                    icon={Plug}
                    tone={activeCount > 0 ? 'success' : 'default'}
                />
            </div>

            <section className='mb-6'>
                <div className='mb-2 flex items-center justify-between'>
                    <h2 className='text-muted-foreground text-sm font-bold'>
                        등록 모델
                    </h2>
                    <Button
                        size='sm'
                        onClick={() =>
                            setDialog({ mode: 'create', model: null })
                        }>
                        <Plus />
                        모델 등록
                    </Button>
                </div>
                {models === null ? (
                    <div className='space-y-2'>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className='h-11 w-full' />
                        ))}
                    </div>
                ) : (
                    <DataTable
                        columns={[
                            '이름',
                            '제공자',
                            '유형',
                            '모델 ID',
                            '용도',
                            'Base URL',
                            '키',
                            '상태',
                            '연결',
                            '작업',
                        ]}
                        rows={models.map((m) => {
                            const result = results[m.id]
                            return [
                                m.name,
                                m.provider,
                                <Badge
                                    key='type'
                                    tone={PROVIDER_TYPE_TONE[m.providerType]}>
                                    {PROVIDER_TYPE_LABEL[m.providerType]}
                                </Badge>,
                                m.modelId,
                                KIND_LABEL[m.kind],
                                m.baseUrl ?? '-',
                                <Badge
                                    key='key'
                                    tone={m.keySet ? 'success' : 'warning'}>
                                    {m.keySet
                                        ? `설정됨${m.keyLast4 ? ` ••••${m.keyLast4}` : ''}`
                                        : '없음'}
                                </Badge>,
                                <Badge
                                    key='status'
                                    tone={m.active ? 'success' : 'neutral'}>
                                    {m.active ? '활성' : '비활성'}
                                </Badge>,
                                <span
                                    key='test'
                                    className='flex items-center gap-1.5'>
                                    <Button
                                        variant='outline'
                                        size='sm'
                                        onClick={() => runTest(m)}
                                        disabled={testing[m.id]}
                                        aria-label={`${m.name} 연결 테스트`}>
                                        {testing[m.id] ? (
                                            <Spinner size='sm' />
                                        ) : (
                                            '연결 테스트'
                                        )}
                                    </Button>
                                    {result ? (
                                        result.ok ? (
                                            <Badge tone='success'>
                                                <Check className='size-3' />
                                                {result.latencyMs}ms
                                            </Badge>
                                        ) : (
                                            <Badge tone='danger'>
                                                <X className='size-3' />
                                                실패
                                            </Badge>
                                        )
                                    ) : null}
                                </span>,
                                <span key='actions' className='flex gap-1'>
                                    <Button
                                        variant='ghost'
                                        size='sm'
                                        onClick={() =>
                                            setDialog({
                                                mode: 'edit',
                                                model: m,
                                            })
                                        }
                                        aria-label={`${m.name} 편집`}>
                                        편집
                                    </Button>
                                    <Button
                                        variant='ghost'
                                        size='sm'
                                        onClick={() => toggleActive(m)}
                                        aria-label={`${m.name} ${m.active ? '비활성화' : '활성화'}`}>
                                        {m.active ? '비활성화' : '활성화'}
                                    </Button>
                                    <Button
                                        variant='ghost'
                                        size='sm'
                                        onClick={() => setPendingDelete(m)}
                                        className='text-destructive hover:text-destructive'
                                        aria-label={`${m.name} 삭제`}>
                                        삭제
                                    </Button>
                                </span>,
                            ]
                        })}
                        emptyMessage='등록된 모델이 없어요. 우측 상단에서 첫 모델을 등록해 주세요.'
                    />
                )}
            </section>

            <section className='border-border bg-muted/30 rounded-md border p-4'>
                <h2 className='text-foreground mb-1 text-sm font-bold'>
                    use-case 모델 바인딩
                </h2>
                <p className='text-muted-foreground max-w-3xl text-[13px]'>
                    어떤 기능이 어떤 모델을 쓰는지(바인딩)는{' '}
                    <Link
                        to='/governance/catalog'
                        className='text-primary font-medium underline-offset-2 hover:underline'>
                        기능 카탈로그
                    </Link>
                    에서 각 기능의 IA 위치·프롬프트와 함께 바로 설정해요. 이
                    페이지는 등록된 모델(레지스트리)을 관리합니다.
                </p>
            </section>

            {dialog ? (
                <ModelFormDialog
                    mode={dialog.mode}
                    initial={dialog.model}
                    busy={saving}
                    onSubmit={onSubmitModel}
                    onClose={() => setDialog(null)}
                />
            ) : null}

            <ConfirmDialog
                open={pendingDelete !== null}
                title='모델 삭제'
                description={
                    pendingDelete
                        ? `'${pendingDelete.name}' 모델을 삭제할까요? 이 작업은 되돌릴 수 없어요.`
                        : undefined
                }
                confirmLabel='삭제'
                danger
                onConfirm={confirmRemoveModel}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    )
}
